import { Field, FieldCheckbox, FieldInput, FieldLabel, FieldSelect } from '@ibe/ui';
import { type ReactNode } from 'react';
import { Controller, type Control, type FieldErrors, type UseFormRegister } from 'react-hook-form';

import { TIMEZONE_OPTIONS } from '../../../lib/forms/timezones.js';
import {
  PMS_PROVIDER_OPTIONS,
  PMS_STATUS_OPTIONS,
  type PmsCredentialFormInput,
} from '../api/schemas.js';

export interface PmsCredentialFieldsProps {
  readonly register: UseFormRegister<PmsCredentialFormInput>;
  readonly errors: FieldErrors<PmsCredentialFormInput>;
  readonly control: Control<PmsCredentialFormInput>;
}

function Section({
  title,
  description,
  children,
}: {
  readonly title: string;
  readonly description?: string;
  readonly children: ReactNode;
}): React.JSX.Element {
  return (
    <fieldset className="space-y-4 border-t border-border pt-4 first:border-t-0 first:pt-0">
      <legend className="sr-only">{title}</legend>
      <div className="space-y-1">
        <h2 className="text-body font-semibold">{title}</h2>
        {description === undefined ? null : (
          <p className="text-caption text-muted-foreground">{description}</p>
        )}
      </div>
      {children}
    </fieldset>
  );
}

function Row({ children }: { readonly children: ReactNode }): React.JSX.Element {
  return <div className="grid grid-cols-1 gap-4 md:grid-cols-2">{children}</div>;
}

export function PmsCredentialFields({
  register,
  errors,
  control,
}: PmsCredentialFieldsProps): React.JSX.Element {
  return (
    <div className="space-y-6">
      <Section
        title="Connection"
        description="How this PMS is identified and which provider it uses."
      >
        <Row>
          <Field error={errors.name?.message} required>
            <FieldLabel>Connection name</FieldLabel>
            <FieldInput autoComplete="off" {...register('name')} />
          </Field>

          <Field error={errors.pmsProvider?.message} required>
            <FieldLabel>PMS type</FieldLabel>
            <Controller
              control={control}
              name="pmsProvider"
              render={({ field }) => (
                <FieldSelect
                  value={field.value}
                  onValueChange={field.onChange}
                  onBlur={field.onBlur}
                  options={PMS_PROVIDER_OPTIONS}
                  placeholder="Select a provider"
                  searchable
                />
              )}
            />
          </Field>
        </Row>

        <Row>
          <Field error={errors.status?.message} required>
            <FieldLabel>Status</FieldLabel>
            <Controller
              control={control}
              name="status"
              render={({ field }) => (
                <FieldSelect
                  value={field.value}
                  onValueChange={field.onChange}
                  onBlur={field.onBlur}
                  options={PMS_STATUS_OPTIONS}
                  placeholder="Select a status"
                  searchable
                />
              )}
            />
          </Field>

          <Field error={errors.useTestEnvironment?.message}>
            <FieldLabel>Use test environment</FieldLabel>
            <Controller
              control={control}
              name="useTestEnvironment"
              render={({ field }) => (
                <FieldCheckbox
                  checked={field.value === 'true'}
                  onCheckedChange={(checked) => {
                    field.onChange(checked ? 'true' : 'false');
                  }}
                  onBlur={field.onBlur}
                >
                  Connect to the provider&apos;s test environment
                </FieldCheckbox>
              )}
            />
          </Field>
        </Row>
      </Section>

      <Section title="Endpoint" description="The base address and local time zone for the PMS.">
        <Row>
          <Field error={errors.baseUrl?.message} required>
            <FieldLabel>Base URL</FieldLabel>
            <FieldInput autoComplete="off" {...register('baseUrl')} />
          </Field>

          <Field error={errors.timezone?.message} required>
            <FieldLabel>Timezone</FieldLabel>
            <Controller
              control={control}
              name="timezone"
              render={({ field }) => (
                <FieldSelect
                  value={field.value}
                  onValueChange={field.onChange}
                  onBlur={field.onBlur}
                  options={TIMEZONE_OPTIONS}
                  placeholder="Select a timezone"
                  searchable
                />
              )}
            />
          </Field>
        </Row>
      </Section>

      <Section
        title="Authentication"
        description="The credentials the platform uses to sign in to the PMS."
      >
        <Row>
          <Field error={errors.agentId?.message} required>
            <FieldLabel>Agent ID (Auth)</FieldLabel>
            <FieldInput autoComplete="off" inputMode="numeric" {...register('agentId')} />
          </Field>

          <Field error={errors.agentSecret?.message} required>
            <FieldLabel>Agent secret</FieldLabel>
            <FieldInput autoComplete="off" type="password" {...register('agentSecret')} />
          </Field>
        </Row>

        <Row>
          <Field error={errors.clientId?.message} required>
            <FieldLabel>Client ID</FieldLabel>
            <FieldInput autoComplete="off" inputMode="numeric" {...register('clientId')} />
          </Field>

          <Field error={errors.clientSecret?.message} required>
            <FieldLabel>Client secret</FieldLabel>
            <FieldInput autoComplete="off" type="password" {...register('clientSecret')} />
          </Field>
        </Row>
      </Section>

      <Section
        title="RMS identifiers"
        description="Property-specific identifiers that map this connection to the provider’s records."
      >
        <Row>
          <Field error={errors.travelAgentId?.message} required>
            <FieldLabel>Travel agent ID</FieldLabel>
            <FieldInput autoComplete="off" inputMode="numeric" {...register('travelAgentId')} />
          </Field>

          <Field error={errors.marketSegmentId?.message} required>
            <FieldLabel>Market segment ID</FieldLabel>
            <FieldInput autoComplete="off" inputMode="numeric" {...register('marketSegmentId')} />
          </Field>
        </Row>

        <Row>
          <Field error={errors.bookingSourceId?.message} required>
            <FieldLabel>Booking source ID</FieldLabel>
            <FieldInput autoComplete="off" inputMode="numeric" {...register('bookingSourceId')} />
          </Field>

          <Field error={errors.partialReservationExpireTime?.message} required>
            <FieldLabel>Partial reservation expire time</FieldLabel>
            <FieldInput
              autoComplete="off"
              inputMode="numeric"
              {...register('partialReservationExpireTime')}
            />
          </Field>
        </Row>

        <Row>
          <Field error={errors.rateGridAgentId?.message}>
            <FieldLabel>Agent ID (Availability / Rate Grid)</FieldLabel>
            <FieldInput autoComplete="off" inputMode="numeric" {...register('rateGridAgentId')} />
          </Field>

          <Field error={errors.masterRateTravelAgentId?.message}>
            <FieldLabel>Travel agent ID (Master Rates)</FieldLabel>
            <FieldInput
              autoComplete="off"
              inputMode="numeric"
              {...register('masterRateTravelAgentId')}
            />
          </Field>
        </Row>

        <Row>
          <Field error={errors.reservationTravelAgentId?.message}>
            <FieldLabel>Travel agent ID (Reservations)</FieldLabel>
            <FieldInput
              autoComplete="off"
              inputMode="numeric"
              {...register('reservationTravelAgentId')}
            />
          </Field>
        </Row>

        <Row>
          <Field
            error={errors.moduleType?.message}
            description="Comma-separated RMS module names, e.g. distribution,availability"
          >
            <FieldLabel>Module types</FieldLabel>
            <FieldInput autoComplete="off" {...register('moduleType')} />
          </Field>
        </Row>
      </Section>
    </div>
  );
}
